import { inject, Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { GatewayId } from '../../site-gateway/models/gateway';
import { DeviceId } from '../models/Device';
import { Device } from '../models/DeviceDetail';
import { SiteId } from '../models/Site';
import { getDeviceGatewayId } from '../utils/gateway';

import { DEVICE_GATEWAY } from './DeviceGateway';
import { SITE_GATEWAY } from './SiteGateway';

@Injectable({ providedIn: 'root' })
export class GatewayResolver {
    private readonly deviceGateway = inject(DEVICE_GATEWAY);
    private readonly siteGateway = inject(SITE_GATEWAY);

    resolveForDevice(deviceId: DeviceId | null | undefined, siteId: SiteId): Observable<GatewayId> {
        if (!deviceId) {
            return this.resolveFromSite(siteId);
        }

        return this.deviceGateway.fetchDevice(deviceId).pipe(
            map((device) => this.getGatewayIdWithinSite(device, siteId)),
            switchMap((gatewayId) => (gatewayId ? of(gatewayId) : this.resolveFromSite(siteId))),
        );
    }

    private getGatewayIdWithinSite(device: Device | null, siteId: SiteId): GatewayId | undefined {
        const deviceSiteId = device?.rawData?.site_id;

        if (deviceSiteId && deviceSiteId !== siteId) {
            return undefined;
        }

        return getDeviceGatewayId(device);
    }

    private resolveFromSite(siteId: SiteId): Observable<GatewayId> {
        return this.siteGateway
            .getSite(siteId)
            .pipe(
                switchMap((site) =>
                    site?.gatewayId
                        ? of(site.gatewayId)
                        : throwError(() => new Error(`No gateway found for site ${siteId}`)),
                ),
            );
    }
}
